import inspectionApi from '../services/api/inspectionApi';
import inspectionSettingsApi from '../services/api/inspectionSettingsApi';
import navigator from '../services/ui/navigator';
import HHSRSScoreApi from '../services/api/HHSRSScoreApi';
import { setAlert } from './alerts';
import { queryClient } from '../services/api/query/queryClient';
import { localise } from '../services/lang/lang';
import { getGetInspectionScoringQueryKey, getGetScoringValidationStatusQueryKey } from '../services/api/generated/client';

export const INSPECTION_REFRESHED = 'INSPECTION_DETAIL/INSPECTION_REFRESHED';
export const INSPECTION_SETTINGS_REFRESHED =
    'INSPECTION_DETAIL/INSPECTION_SETTINGS_REFRESHED';
export const VISIBLE_MODAL_TOGGLED = 'INSPECTION_DETAIL/VISIBLE_MODAL_TOGGLED';
export const TOKEN_REFRESHED = 'INSPECTION_DETAIL/TOKEN_REFRESHED';
export const IS_EXPORTING_REFRESHED =
    'INSPECTION_DETAIL/IS_EXPORTING_REFRESHED';
export const HHSRS_SCORING_REFRESHED =
    'INSPECTION_DETAIL/HHSRS_SCORING_REFRESHED';
export const HHSRS_SCORING_START_STATUS_REFRESHED =
    'INSPECTION_DETAIL/HHSRS_SCORING_START_STATUS_REFRESHED';
export const HHSRS_SCORING_VALIDATION_STATUS_REFRESHED =
    'INSPECTION_DETAIL/HHSRS_SCORING_VALIDATION_STATUS_REFRESHED';
export const RESET = 'INSPECTION_DETAIL/RESET';

export const visibleModalToggled = (visibleModal) => ({
    type: VISIBLE_MODAL_TOGGLED,
    visibleModal,
});

export const inspectionRefreshed = (inspection) => ({
    type: INSPECTION_REFRESHED,
    inspection,
});

export const inspectionSettingsRefreshed = (inspectionSettings) => ({
    type: INSPECTION_SETTINGS_REFRESHED,
    inspectionSettings,
});

export const HHSRSScoringRefreshed = (HHSRSScoring) => ({
    type: HHSRS_SCORING_REFRESHED,
    HHSRSScoring,
});

export const HHSRSScoringStartStatusRefreshed = (hasStartedHHSRSScoring) => ({
    type: HHSRS_SCORING_START_STATUS_REFRESHED,
    hasStartedHHSRSScoring,
});

export const HHSRSValidationStatusRefreshed = (
    isInspectionValidForHHSRSScoring
) => ({
    type: HHSRS_SCORING_VALIDATION_STATUS_REFRESHED,
    isInspectionValidForHHSRSScoring,
});

export const tokenRefreshed = (token) => ({
    type: TOKEN_REFRESHED,
    token,
});

export const reset = () => ({ type: RESET });

export const isExportingRefreshed = (isExporting) => ({
    type: IS_EXPORTING_REFRESHED,
    isExporting,
});

export const getInspection = (id) => async (dispatch) => {
    try {
        const {
            data: { data },
        } = await inspectionApi.getOne(id);
        dispatch(inspectionRefreshed(data));
    } catch ({ response }) {
        navigator.push('/not-found');
    }
};

export const getInspectionByShareToken = (token) => async (dispatch) => {
    try {
        const {
            data: { data },
        } = await inspectionApi.getOneByShareToken(token);
        dispatch(tokenRefreshed(token));
        dispatch(inspectionRefreshed(data));
    } catch ({ response }) {
        navigator.push('/');
    }
};

export const getInspectionByExportToken = (token) => async (dispatch) => {
    try {
        const {
            data: { data },
        } = await inspectionApi.getOneByExportToken(token);
        dispatch(tokenRefreshed(token));
        dispatch(inspectionRefreshed(data));
    } catch ({ response }) {
        navigator.push('/');
    }
};

export const getInspectionSettings = (inspectionId) => async (dispatch) => {
    try {
        const {
            data: { data },
        } = await inspectionSettingsApi.getOneByInspection(inspectionId);
        dispatch(inspectionSettingsRefreshed(data));
    } catch ({ response }) {
        dispatch(setAlert('Unable to fetch inspection settings.', 'error'));
    }
};

export const getHHSRSScoring = (inspectionId) => async (dispatch) => {
    try {
        const {
            data: { data },
        } = await HHSRSScoreApi.getOneByInspection(inspectionId);

        const hasStartedScoring = data.some(
            (hazard) => hazard.scoring !== null
        );

        dispatch(HHSRSScoringStartStatusRefreshed(hasStartedScoring));
        dispatch(HHSRSScoringRefreshed(data));
    } catch ({ response }) {
        dispatch(setAlert('Unable to fetch HHSRS scoring.', 'error'));
    }
};

export const removeHHSRSScoring = (inspectionId) => async (dispatch) => {
    try {
        await HHSRSScoreApi.remove(inspectionId);

        dispatch(HHSRSScoringStartStatusRefreshed(false));
        dispatch(HHSRSScoringRefreshed(null));

    } catch ({ response }) {
        dispatch(setAlert('Unable to remove HHSRS scoring.', 'error'));
    }
};

export const getHHSRSValidationStatus =
    (inspectionId, isInitialCheck = false) =>
    async (dispatch) => {
        try {
            const {
                data: { data },
            } = await HHSRSScoreApi.getValidationStatus(inspectionId);
            dispatch(HHSRSValidationStatusRefreshed(data));
            if (!data && !isInitialCheck) {
                dispatch(removeHHSRSScoring(inspectionId));
            }
        } catch ({ response }) {
            dispatch(
                setAlert(
                    'Unable to fetch HHSRS scoring validation status.',
                    'error'
                )
            );
        }
    };

export const getInspectionExport = (inspectionId) => async (dispatch) => {
    dispatch(isExportingRefreshed(true));
    try {
        const data = await inspectionApi.getExport(inspectionId);

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            const fileName = data.headers['content-disposition']
                .split('filename=')[1]
                .split(';')[0];
            window.navigator.msSaveOrOpenBlob(data.data, fileName);
        } else {
            const url = window.URL.createObjectURL(data.data);
            window.open(url);
        }
    } catch ({ response }) {
        dispatch(setAlert('Unable to export inspection.', 'error'));
    } finally {
        dispatch(isExportingRefreshed(false));
    }
};
